// Models
import cloneDeep from "lodash/cloneDeep";
import { Temporal } from "temporal-polyfill-lite";
import { PatchableAndDeltaSyncableODataEntity } from "../common/PatchableAndDeltaSyncableODataEntity";
import { CustomFieldChoice } from "./CustomFieldChoice";
/**
 * The custom field definition entity implementation.
 */ export class CustomFieldDefinition extends PatchableAndDeltaSyncableODataEntity {
    getPropertyMappingOverride() {
        return {
            choiceOptions: "choiceOptions@delta"
        };
    }
    mapEntityChangesToResource(changed, source) {
        const resourceKeyMap = this.getChangePropertyMapping();
        return {
            [resourceKeyMap.displayName]: changed.displayName,
            [resourceKeyMap.rollupType]: changed.rollupType,
            [resourceKeyMap.choiceOptions]: this.processSubEntityArray(this.choiceOptions, source.choiceOptions)
        };
    }
    mapDeltaChangesToEntity(delta) {
        return this.processDeltaWithTranslators(delta, {
            archivalInfo: (value)=>({
                    ...value,
                    modifiedDate: value.modifiedDate ? Temporal.PlainDate.from(value.modifiedDate) : null
                }),
            choiceOptions: (deltaValue)=>this.applySubEntityArrayDelta(deltaValue, this.choiceOptions),
            createdDateTime: (value)=>Temporal.Instant.from(value),
            lastModifiedDateTime: (value)=>Temporal.Instant.from(value),
            deletedDateTime: (value)=>value ? Temporal.Instant.from(value) : null
        });
    }
    static fromResource(resource) {
        return new CustomFieldDefinition({
            id: resource.id,
            version: resource["@etag"],
            archivalInfo: {
                isArchived: resource.archivalInfo.isArchived,
                justification: resource.archivalInfo.justification,
                modifiedBy: resource.archivalInfo.modifiedBy,
                modifiedDate: resource.archivalInfo.modifiedDate ? Temporal.PlainDate.from(resource.archivalInfo.modifiedDate) : null
            },
            choiceOptions: resource.choiceOptions.map((choice)=>CustomFieldChoice.fromResource(choice)),
            createdBy: resource.createdBy,
            createdDateTime: Temporal.Instant.from(resource.createdDateTime),
            lastModifiedBy: resource.lastModifiedBy,
            lastModifiedDateTime: Temporal.Instant.from(resource.lastModifiedDateTime),
            deletedBy: resource.deletedBy,
            deletedDateTime: resource.deletedDateTime ? Temporal.Instant.from(resource.deletedDateTime) : null,
            displayName: resource.displayName,
            planId: resource.planId,
            valueType: resource.valueType,
            rollupType: resource.rollupType
        });
    }
    getClone() {
        return new CustomFieldDefinition({
            id: this.id,
            version: this.version,
            archivalInfo: {
                ...cloneDeep(this.archivalInfo),
                modifiedDate: this.archivalInfo.modifiedDate ? Temporal.PlainDate.from(this.archivalInfo.modifiedDate) : null
            },
            choiceOptions: this.choiceOptions.map((choice)=>choice.getClone()),
            createdBy: cloneDeep(this.createdBy),
            createdDateTime: Temporal.Instant.from(this.createdDateTime),
            lastModifiedBy: cloneDeep(this.lastModifiedBy),
            lastModifiedDateTime: Temporal.Instant.from(this.lastModifiedDateTime),
            deletedBy: this.deletedBy ? cloneDeep(this.deletedBy) : null,
            deletedDateTime: this.deletedDateTime ? Temporal.Instant.from(this.deletedDateTime) : null,
            displayName: this.displayName,
            planId: this.planId,
            valueType: this.valueType,
            rollupType: this.rollupType
        });
    }
    setProperty(key, value) {
        const clone = this.getClone();
        clone[key] = value;
        return clone;
    }
    forSerialization() {
        return {
            id: this.id,
            "@etag": this.version,
            archivalInfo: {
                isArchived: this.archivalInfo.isArchived,
                justification: this.archivalInfo.justification,
                modifiedBy: this.archivalInfo.modifiedBy,
                modifiedDate: this.archivalInfo.modifiedDate?.toString() ?? null
            },
            choiceOptions: this.choiceOptions.map((choice)=>choice.forSerialization()),
            createdBy: this.createdBy,
            createdDateTime: this.createdDateTime.toString(),
            lastModifiedBy: this.lastModifiedBy,
            lastModifiedDateTime: this.lastModifiedDateTime.toString(),
            deletedBy: this.deletedBy,
            deletedDateTime: this.deletedDateTime?.toString() ?? null,
            displayName: this.displayName,
            planId: this.planId,
            valueType: this.valueType,
            rollupType: this.rollupType
        };
    }
    forCreate() {
        return {
            choiceOptions: this.choiceOptions.map((choice)=>choice.forCreate()),
            displayName: this.displayName,
            planId: this.planId,
            rollupType: this.rollupType,
            valueType: this.valueType
        };
    }
    constructor(propertyBag){
        super(propertyBag);
        this.archivalInfo = propertyBag.archivalInfo;
        this.choiceOptions = propertyBag.choiceOptions;
        this.createdBy = propertyBag.createdBy;
        this.createdDateTime = propertyBag.createdDateTime;
        this.lastModifiedBy = propertyBag.lastModifiedBy;
        this.lastModifiedDateTime = propertyBag.lastModifiedDateTime;
        this.deletedBy = propertyBag.deletedBy;
        this.deletedDateTime = propertyBag.deletedDateTime;
        this.displayName = propertyBag.displayName;
        this.planId = propertyBag.planId;
        this.valueType = propertyBag.valueType;
        this.rollupType = propertyBag.rollupType;
    }
}
